import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPlayersRanking, getServers } from '../api';
import PlayerCard from '../components/PlayerCard';
import SkinViewer from '../components/SkinViewer';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

// Floating particles component using pure CSS animations
function FloatingParticles() {
  const blocks = ['⛏️', '💎', '🪨', '⬛', '🟫', '🟨', '✨'];
  const particles = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    emoji: blocks[i % blocks.length],
    left: Math.random() * 100,
    size: 8 + Math.random() * 16,
    delay: Math.random() * 8,
    duration: 6 + Math.random() * 8,
    drift: -30 + Math.random() * 60,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
      {particles.map(p => (
        <span
          key={p.id}
          className="absolute animate-float-up"
          style={{
            left: `${p.left}%`,
            bottom: '-20px',
            fontSize: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--drift': `${p.drift}px`,
            userSelect: 'none',
          }}
        >
          {p.emoji}
        </span>
      ))}
    </div>
  );
}

// Hook for intersection observer animations
function useScrollReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('animate-revealed');
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function ScrollReveal({ children, delay = 0, className = '' }) {
  const ref = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`animate-reveal-base ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [topPlayers, setTopPlayers] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rankingData, serversData] = await Promise.all([
        getPlayersRanking(1, 10),
        getServers().catch(() => []),
      ]);
      setTopPlayers(rankingData.players || []);
      setServers(serversData || []);
    } catch (err) {
      setError(err.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <LoadingSpinner text="Cargando estadísticas..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchData} />;

  const top3 = topPlayers.slice(0, 3);
  const restPlayers = topPlayers.slice(3);

  const podiumOrder = top3.length === 3 ? [top3[1], top3[0], top3[2]] : top3;
  const podiumHeights = ['h-28', 'h-40', 'h-20'];
  const isMobile = window.innerWidth < 768;
  const skinSizes = isMobile
    ? [{ w: 80, h: 140 }, { w: 100, h: 170 }, { w: 70, h: 120 }]
    : [{ w: 130, h: 220 }, { w: 160, h: 280 }, { w: 120, h: 200 }];

  return (
    <div className="space-y-10">
      {/* Hero with particles */}
      <section className="relative text-center pt-12 pb-8 overflow-hidden">
        <FloatingParticles />
        <div className="relative z-10">
          <h1 className="mc-title text-mc-accent text-4xl md:text-5xl mb-4 animate-fade-up">
            ⛏️ OnStats
          </h1>
          <p className="text-mc-muted text-lg md:text-xl max-w-xl mx-auto animate-fade-up animation-delay-200">
            Estadísticas de minería en tiempo real. Descubre quién domina los bloques.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3 animate-fade-up animation-delay-400">
            <button
              onClick={() => navigate('/rankings')}
              className="group px-8 py-3 bg-mc-accent text-mc-darker font-bold rounded-xl hover:shadow-lg hover:shadow-mc-accent/25 transition-all duration-300 hover:scale-105"
            >
              Ver Rankings
            </button>
            <button
              onClick={() => navigate('/download')}
              className="px-8 py-3 mc-border bg-mc-card text-mc-emerald font-bold rounded-xl hover:border-mc-emerald/50 hover:shadow-lg hover:shadow-mc-emerald/10 transition-all duration-300 hover:scale-105"
            >
              🔌 Descargar Plugin
            </button>
          </div>
        </div>
      </section>

      {/* Top 3 Podium - Fantasy */}
      {top3.length >= 3 && (
        <ScrollReveal>
          <section className="podium-section rounded-2xl p-6 md:p-10 relative overflow-hidden">
            {/* Background stars */}
            {Array.from({ length: 15 }, (_, i) => (
              <div
                key={`star-${i}`}
                className="star-bg"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDuration: `${2 + Math.random() * 4}s`,
                  animationDelay: `${Math.random() * 3}s`,
                }}
              />
            ))}



            <div className="relative z-10">
              <h2 className="mc-title text-mc-gold text-center text-xl md:text-2xl mb-10" style={{ textShadow: '0 0 20px rgba(251,191,36,0.3), 2px 2px 0 #000' }}>
                👑 Top Mineros
              </h2>

              <div className="flex items-end justify-center gap-3 md:gap-8">
                {podiumOrder.map((player, idx) => {
                  const barStyles = ['podium-bar-silver', 'podium-bar-gold', 'podium-bar-bronze'];
                  const glowStyles = ['username-glow-silver', 'username-glow-gold', 'username-glow-bronze'];
                  const rankEmojis = ['🥈', '🥇', '🥉'];
                  return (
                    <div
                      key={player.username}
                      className={`flex flex-col items-center cursor-pointer group animate-fade-up ${idx === 1 ? 'podium-winner' : ''}`}
                      style={{ animationDelay: `${300 + idx * 200}ms` }}
                      onClick={() => navigate(`/player/${player.username}`)}
                    >
                      {/* Rank emoji above skin */}
                      <div className="text-2xl md:text-3xl mb-1">{rankEmojis[idx]}</div>

                      {/* 3D Skin */}
                      <div className="mb-3 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-3">
                        <SkinViewer
                          username={player.username}
                          width={skinSizes[idx].w}
                          height={skinSizes[idx].h}
                          animation="idle"
                        />
                      </div>

                      {/* Username with glow */}
                      <p className={`font-bold mb-1 truncate max-w-[120px] transition-all duration-300 ${idx === 1 ? 'text-mc-gold text-base' : 'text-mc-text text-sm'} ${glowStyles[idx]} group-hover:scale-110`}>
                        {player.username}
                      </p>

                      {/* Block count with shimmer */}
                      <p className="text-mc-accent text-xs font-bold mb-3">
                        {player.mined_blocks?.toLocaleString('es-DO')} bloques
                      </p>

                      {/* Podium bar with glow */}
                      <div
                        className={`w-24 md:w-36 ${podiumHeights[idx]} ${barStyles[idx]} rounded-t-xl flex items-center justify-center animate-grow-up relative overflow-hidden`}
                        style={{ animationDelay: `${800 + idx * 200}ms` }}
                      >
                        {/* Shimmer overlay on bar */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-fade-up" style={{ animationDelay: `${1200 + idx * 200}ms` }} />
                        <span className="mc-title text-white text-sm md:text-lg drop-shadow-lg relative z-10">
                          {idx === 1 ? '1°' : idx === 0 ? '2°' : '3°'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* Stats summary */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { emoji: '⛏️', color: 'mc-diamond', value: top3[0]?.mined_blocks?.toLocaleString('es-DO') || '—', label: 'Bloques del #1' },
          { emoji: '👥', color: 'mc-emerald', value: `${topPlayers.length}+`, label: 'Jugadores activos' },
          { emoji: '🖥️', color: 'mc-gold', value: servers.length, label: 'Servidores' },
        ].map((stat, i) => (
          <ScrollReveal key={i} delay={i * 100}>
            <div className={`mc-border rounded-xl p-6 bg-mc-card text-center hover:border-${stat.color}/30 hover:scale-105 transition-all duration-300 cursor-default`}>
              <p className={`text-3xl font-bold text-${stat.color}`}>{stat.emoji}</p>
              <p className="text-2xl font-bold text-mc-text mt-2">{stat.value}</p>
              <p className="text-mc-muted text-sm mt-1">{stat.label}</p>
            </div>
          </ScrollReveal>
        ))}
      </section>

      {/* Rest of top 10 */}
      {restPlayers.length > 0 && (
        <section>
          <ScrollReveal>
            <div className="flex items-center justify-between mb-6">
              <h2 className="mc-title text-mc-emerald text-lg">📊 Top 4-10</h2>
              <button onClick={() => navigate('/rankings')} className="text-mc-accent text-sm hover:underline">
                Ver todos →
              </button>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {restPlayers.map((p, i) => (
              <ScrollReveal key={p.username} delay={i * 80}>
                <PlayerCard
                  rank={p.rank}
                  username={p.username}
                  blocks={p.mined_blocks}
                  onClick={() => navigate(`/player/${p.username}`)}
                />
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {/* Servers */}
      {servers.length > 0 && (
        <section>
          <ScrollReveal>
            <h2 className="mc-title text-mc-diamond text-lg mb-6">🖥️ Servidores</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {servers.map((s, i) => (
              <ScrollReveal key={s.id} delay={i * 100}>
                <div className="mc-border rounded-xl p-5 bg-mc-card hover:border-mc-accent/30 hover:scale-[1.02] transition-all duration-300">
                  <div className="flex items-center gap-3">
                    {s.picture_url ? (
                      <img src={s.picture_url} alt={s.name} className="w-10 h-10 rounded-lg" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-mc-darker flex items-center justify-center text-xl">🖥️</div>
                    )}
                    <div>
                      <p className="text-mc-text font-semibold">{s.name}</p>
                      {s.description && <p className="text-mc-muted text-xs mt-0.5">{s.description}</p>}
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
